import { Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { VOCABULARY_ENUMS, type EnumCount, type EnumCounts, type VocabularyEnumKey } from './types'
import { useStudioLocale } from '@/i18n/use-studio-locale'

export interface EnumNavProps {
  activeEnum: VocabularyEnumKey
  counts: EnumCounts
  searchQuery: string
  onSelectEnum: (key: VocabularyEnumKey) => void
  onSearchChange: (q: string) => void
}

/**
 * `VOCABULARY_ENUMS[].label` is the English name of the shard, part of the
 * catalogue's data. The reader's word for it is dictionary copy, so the nav
 * resolves one from the other instead of rendering the data straight through.
 */
function labelFor(
  key: VocabularyEnumKey,
  copy: {
    enumPrefix: string
    enumAccessor: string
    enumEvent: string
    enumAction: string
  },
): string {
  switch (key) {
    case 'prefix':
      return copy.enumPrefix
    case 'accessor':
      return copy.enumAccessor
    case 'event':
      return copy.enumEvent
    case 'action':
      return copy.enumAction
  }
}

export function EnumNav({
  activeEnum,
  counts,
  searchQuery,
  onSelectEnum,
  onSearchChange,
}: EnumNavProps) {
  const { strings } = useStudioLocale()
  const copy = strings.designSystem.vocabularyPanel
  return (
    <div className="flex h-full w-[200px] flex-col gap-3 border-r border-border p-3">
      <div className="flex flex-col gap-1">
        <div className="px-2 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {copy.title}
        </div>
        {VOCABULARY_ENUMS.map((descriptor) => {
          const active = descriptor.key === activeEnum
          const count = counts[descriptor.key]
          return (
            <button
              key={descriptor.key}
              type="button"
              role="button"
              aria-current={active ? 'true' : undefined}
              onClick={() => onSelectEnum(descriptor.key)}
              className={cn(
                'flex items-center justify-between rounded-md px-2 py-1.5 text-sm font-medium transition-colors',
                active
                  ? 'bg-secondary text-secondary-foreground'
                  : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground',
              )}
            >
              <span>{labelFor(descriptor.key, copy)}</span>
              <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs font-mono">
                {badgeText(count)}
              </span>
            </button>
          )
        })}
      </div>
      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder={copy.search}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="h-8 pl-7 text-sm"
        />
      </div>
    </div>
  )
}

/**
 * The badge glyph for one counter.
 *
 * A number means the query came back and returned that many rows — `0`
 * included, which is a fact about the shard and not a placeholder. `…` means
 * the answer is not in yet. `!` means that enum's query failed; selecting the
 * tab shows the error and a retry. Glyphs rather than words, deliberately:
 * this panel is not localised yet, and a counter should not be the thing that
 * introduces new English into a Korean UI.
 */
function badgeText(count: EnumCount): string {
  switch (count.kind) {
    case 'ready':
      return String(count.count)
    case 'error':
      return '!'
    case 'pending':
      return '…'
  }
}
